import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, Upload, message, Divider, Alert } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import axios from 'axios';
import useBrandingStore from '../../store/useBrandingStore';

const { Title } = Typography;
const { Dragger } = Upload;

const CompanyDetails = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [invoiceSettings, setInvoiceSettings] = useState({});
    const { updateBranding } = useBrandingStore();

    const fetchInfo = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [companyRes, settingsRes] = await Promise.all([
                axios.get('/api/company', { headers: { Authorization: `Bearer ${token} ` } }),
                axios.get('/api/settings/invoice', { headers: { Authorization: `Bearer ${token} ` } })
            ]);

            form.setFieldsValue({
                name: companyRes.data.name,
                address: companyRes.data.address,
                phone: companyRes.data.phone,
                email: companyRes.data.email,
                zip: companyRes.data.zip,
                town: companyRes.data.town,
                country: companyRes.data.country_code,
                vat_number: companyRes.data.tva_intra,
            });
            setInvoiceSettings(settingsRes.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInfo();
    }, []);

    const onFinish = async (values) => {
        setSaveLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Map back to Dolibarr fields
            const updateData = {
                name: values.name,
                address: values.address,
                phone: values.phone,
                email: values.email,
                zip: values.zip,
                town: values.town,
                country_code: values.country,
                tva_intra: values.vat_number
            };

            await axios.put('/api/company', updateData, {
                headers: { Authorization: `Bearer ${token} ` }
            });
            updateBranding({ company_name: values.name });
            message.success('Company info updated successfully');
        } catch (error) {
            message.error('Failed to update company info');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleUploadDefault = async (options) => {
        const { onSuccess, onError, file } = options;
        const fmData = new FormData();
        fmData.append("logo", file);
        const token = localStorage.getItem('token');

        try {
            const res = await axios.post('/api/company/logo', fmData, {
                headers: {
                    Authorization: `Bearer ${token} `,
                    'Content-Type': 'multipart/form-data'
                }
            });
            onSuccess(res.data);
        } catch (err) {
            onError({ err });
        }
    };

    const onUploadChange = async (info) => {
        const { status } = info.file;
        if (status === 'done') {
            message.success(`${info.file.name} file uploaded successfully.`);
            const newLogoUrl = info.file.response.url;

            // Auto-save to settings
            try {
                const token = localStorage.getItem('token');
                // Ensure we pass the full settings object
                const newSettings = { ...invoiceSettings, logo_url: newLogoUrl };

                await axios.put('/api/settings/invoice', newSettings, {
                    headers: { Authorization: `Bearer ${token} ` }
                });
                setInvoiceSettings(newSettings);
                updateBranding({ logo_url: newLogoUrl });
                message.success('Logo saved to configuration');
            } catch (err) {
                message.error('Failed to save logo reference');
            }
        } else if (status === 'error') {
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    return (
        <Card title="Company Information" bordered={false} style={{ background: 'transparent' }}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item name="name" label="Company Name" style={{ flex: 2 }} rules={[{ required: true }]}>
                        <Input placeholder="Enter company name" />
                    </Form.Item>
                    <Form.Item name="vat_number" label="VAT Number" style={{ flex: 1 }}>
                        <Input placeholder="Enter VAT number" />
                    </Form.Item>
                </div>

                <Form.Item name="address" label="Address">
                    <Input.TextArea rows={2} placeholder="Full address" />
                </Form.Item>

                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item name="zip" label="Zip" style={{ flex: 1 }}>
                        <Input placeholder="Zip" />
                    </Form.Item>
                    <Form.Item name="town" label="Town" style={{ flex: 2 }}>
                        <Input placeholder="Town" />
                    </Form.Item>
                    <Form.Item name="country" label="Country Code" style={{ flex: 1 }}>
                        <Input placeholder="e.g. JO" />
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item name="phone" label="Phone" style={{ flex: 1 }}>
                        <Input placeholder="Phone number" />
                    </Form.Item>
                    <Form.Item name="email" label="Email" style={{ flex: 1 }}>
                        <Input placeholder="Email address" />
                    </Form.Item>
                </div>

                <div style={{ marginBottom: 24 }}>
                    <Button type="primary" htmlType="submit" loading={saveLoading}>
                        Save Company Details
                    </Button>
                </div>

                <Divider>Branding & Logo</Divider>
                <div style={{ marginBottom: 16 }}>
                    <p style={{ marginBottom: 8 }}>Company Logo</p>
                    <Dragger
                        name="logo"
                        customRequest={handleUploadDefault}
                        onChange={onUploadChange}
                        showUploadList={false}
                        accept="image/*"
                    >
                        {invoiceSettings.logo_url ? (
                            <div style={{ padding: 20 }}>
                                <img src={`/api${invoiceSettings.logo_url}`} alt="logo" style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }} />
                                <p className="ant-upload-text">Click or drag file to replace</p>
                            </div>
                        ) : (
                            <div style={{ padding: 20 }}>
                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                <p className="ant-upload-text">Click or drag file to upload logo</p>
                            </div>
                        )}
                    </Dragger>
                </div>
            </Form>
        </Card>
    );
};

export default CompanyDetails;
